// Календарь: месячная сетка со сроками задач и окончаниями договоров.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, CheckSquare, FileText } from 'lucide-react';
import { tasksApi } from '@/api';
import { AppLayout, PageHeader } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/Button';
import type { CalendarEvent } from '@/types';
import './calendar.css';

const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function CalendarPage() {
  const [current, setCurrent] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const year = current.getFullYear();
  const month = current.getMonth();
  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month + 1, 0);

  const { data: events } = useQuery({
    queryKey: ['calendar', year, month],
    queryFn: () => tasksApi.calendar(ymd(monthStart), ymd(monthEnd)),
  });

  // События по дате
  const byDate = new Map<string, CalendarEvent[]>();
  (events ?? []).forEach((e) => {
    const arr = byDate.get(e.date) ?? [];
    arr.push(e);
    byDate.set(e.date, arr);
  });

  // Сетка: начинаем с понедельника недели, в которую попадает 1-е число
  const firstWeekday = (monthStart.getDay() + 6) % 7; // 0=Пн
  const cells: (Date | null)[] = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= monthEnd.getDate(); d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const today = ymd(new Date());

  return (
    <AppLayout>
      <PageHeader title="Календарь" subtitle="Сроки задач и окончание договоров" />

      <div className="cal-toolbar">
        <Button variant="ghost" onClick={() => setCurrent(new Date(year, month - 1, 1))}><ChevronLeft size={18} /></Button>
        <span className="cal-toolbar__title">{MONTHS[month]} {year}</span>
        <Button variant="ghost" onClick={() => setCurrent(new Date(year, month + 1, 1))}><ChevronRight size={18} /></Button>
        <Button variant="ghost" onClick={() => { const d = new Date(); setCurrent(new Date(d.getFullYear(), d.getMonth(), 1)); }}>Сегодня</Button>
      </div>

      <div className="cal-grid">
        {WEEKDAYS.map((w) => <div key={w} className="cal-weekday">{w}</div>)}
        {cells.map((date, i) => {
          if (!date) return <div key={i} className="cal-cell cal-cell--empty" />;
          const key = ymd(date);
          const dayEvents = byDate.get(key) ?? [];
          return (
            <div key={i} className={`cal-cell${key === today ? ' cal-cell--today' : ''}`}>
              <div className="cal-cell__num">{date.getDate()}</div>
              <div className="cal-cell__events">
                {dayEvents.slice(0, 4).map((e, j) => (
                  <div key={j}
                    className={`cal-event cal-event--${e.type}${e.overdue ? ' cal-event--overdue' : ''}${e.status === 'done' ? ' cal-event--done' : ''}`}
                    title={e.title}>
                    {e.type === 'task' ? <CheckSquare size={10} /> : <FileText size={10} />}
                    <span className="cal-event__text">{e.title}</span>
                  </div>
                ))}
                {dayEvents.length > 4 && <div className="cal-more">+{dayEvents.length - 4}</div>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="cal-legend">
        <span><span className="cal-dot cal-dot--task" /> Задача</span>
        <span><span className="cal-dot cal-dot--contract_deadline" /> Срок договора</span>
        <span><span className="cal-dot cal-dot--overdue" /> Просрочено</span>
      </div>
    </AppLayout>
  );
}
